import { castToDecimal } from '@fruitful/fruitils';
import {
    type DeepPartial,
    type GroupWithEntityType,
    isRetirementRegistrationType,
    type MoneyMapAccount,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountDetails,
    MoneyMapCategory,
    MoneyMapCategoryToMoneyMapRuleType,
    type MoneyMapRule,
    MoneyMapRuleContributionType,
    type MoneyMapRuleGroup,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    type MoneyMapRuleWithAccount,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    PaycheckRoutingRuleType,
    type RuleOrGroup,
    type RuleWithEntityType,
} from '@fruitful/types';
import { keyBy, omit, orderBy, uniqBy } from 'lodash';

import { getAccountWithDetailsForRule } from './moneyMapAccountUtils';

/**
 * Whether a "target balance" (until-available-balance) stopping condition can
 * be set for a goal routed to this account type. Mirrors Grove's goal editor,
 * which locks external and investment-management accounts to "contribute
 * indefinitely" (no target balance) — see GoalSettingsFormModule.tsx.
 */
export const isTargetBalanceEligibleAccountType = (
    accountType: MoneyMapAccountType,
): boolean => {
    const isExternalAccount =
        accountType === MoneyMapAccountType.EXTERNAL_BANKING ||
        accountType === MoneyMapAccountType.EXTERNAL_HOLISTIC;
    const isInvestmentAccount =
        accountType === MoneyMapAccountType.INVESTMENT_MANAGEMENT ||
        accountType === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;
    return !isExternalAccount && !isInvestmentAccount;
};

/** Keep rules' embedded account in sync with income when they reference the same money map account. */
export const syncIncomeMoneyMapAccountToRulesSharingAccountId = (
    rules: Array<DeepPartial<MoneyMapRuleWithAccount>>,
    incomeMoneyMapAccount: DeepPartial<MoneyMapAccount>,
    accountId: string,
): Array<DeepPartial<MoneyMapRuleWithAccount>> =>
    rules.map((rule) => {
        const ruleAccountId =
            rule.moneyMapAccountId ?? rule.moneyMapAccount?.id;
        if (ruleAccountId !== accountId) {
            return rule;
        }
        return {
            ...rule,
            moneyMapAccountId: accountId,
            moneyMapAccount: incomeMoneyMapAccount,
        };
    });

export const getRulesWithAccountsForCategory = (
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
    accounts: Record<string, MoneyMapAccountWithAccountDetails>,
    category: MoneyMapCategory,
): Array<DeepPartial<MoneyMapRuleWithAccountDetails>> =>
    (moneyMap?.moneyMapRules ?? [])
        .filter(
            (rule) =>
                rule?.type === MoneyMapCategoryToMoneyMapRuleType[category],
        )
        .map((rule) => {
            const baseAccount = getAccountWithDetailsForRule(
                rule,
                accounts,
                moneyMap,
            );
            const hasRetirementContributionLimit =
                baseAccount?.type ===
                    MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
                isRetirementRegistrationType(
                    (
                        baseAccount?.accountDetails as
                            | MoneyMapApexAccountDetails
                            | undefined
                    )?.apexAccountType,
                );
            return {
                ...rule,
                moneyMapAccount:
                    baseAccount != null
                        ? { ...baseAccount, hasRetirementContributionLimit }
                        : baseAccount,
            };
        });

export const getTotalAmountCentsForRules = (
    rules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
) =>
    rules.reduce((total, rule) => {
        const {
            routingRuleType,
            targetBalanceCents,
            monthlyContributionMaxCents,
        } = rule;

        // routingRuleType is authoritative for which field holds the "amount" --
        // targetBalanceCents only applies to MINIMUM_BALANCE rules (BILLS monthly
        // figure *and* real GOAL targets) and monthlyContributionMaxCents only
        // applies to PERCENTAGE rules (SPEND). Some legacy rules have both fields
        // populated (one stale), so don't fall back blindly.
        // See PaycheckRoutingRuleType.
        if (routingRuleType === PaycheckRoutingRuleType.MINIMUM_BALANCE) {
            return total + (targetBalanceCents ?? 0);
        }
        if (routingRuleType === PaycheckRoutingRuleType.PERCENTAGE) {
            return total + (monthlyContributionMaxCents ?? 0);
        }

        return total + (targetBalanceCents ?? monthlyContributionMaxCents ?? 0);
    }, 0);

export const findRuleByIdentifier = (
    rules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
    identifier?: string | null,
): DeepPartial<MoneyMapRuleWithAccountDetails> | undefined =>
    identifier != null
        ? rules.find(
              (rule) =>
                  rule.id === identifier || rule.description === identifier,
          )
        : undefined;

export const findGroupById = (
    groups: Array<DeepPartial<MoneyMapRuleGroup>>,
    id?: string | null,
): DeepPartial<MoneyMapRuleGroup> | undefined =>
    id != null ? groups.find((group) => group.id === id) : undefined;

// Get the insertion priority for a new top-level rule.
// Top-level sections are ordered: Bills -> Spend -> Goals -> Other accounts.
// Goal groups count as a single top-level item, so only ungrouped ("loose") goals
// are counted individually when determining the next goal or account-only slot.
export const calculateNewRulePriority = (
    billRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
    spendRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
    goalRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
    newRuleType: MoneyMapRuleType,
    goalGroups?: Array<DeepPartial<MoneyMapRuleGroup>>,
    otherAccountRules?: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
): number => {
    switch (newRuleType) {
        case MoneyMapRuleType.BILLS:
            return billRules.length + 1;
        case MoneyMapRuleType.SPEND:
            return billRules.length + spendRules.length + 1;
        case MoneyMapRuleType.GOAL:
            // Goals are inserted after bills and spend, but before Other accounts.
            if (goalGroups) {
                const looseGoalRules = goalRules.filter(
                    (rule) => rule.moneyMapRuleGroupId == null,
                );

                return (
                    billRules.length +
                    spendRules.length +
                    goalGroups.length +
                    looseGoalRules.length +
                    1
                );
            }

            return billRules.length + spendRules.length + goalRules.length + 1;
        case MoneyMapRuleType.ACCOUNT_ONLY:
        default:
            // Other accounts are always inserted last, after all top-level goal items.
            if (goalGroups) {
                const looseGoalRules = goalRules.filter(
                    (rule) => rule.moneyMapRuleGroupId == null,
                );

                return (
                    billRules.length +
                    spendRules.length +
                    goalGroups.length +
                    looseGoalRules.length +
                    (otherAccountRules?.length ?? 0) +
                    1
                );
            }

            return (
                billRules.length +
                spendRules.length +
                goalRules.length +
                (otherAccountRules?.length ?? 0) +
                1
            );
    }
};

// MoneyMapRuleContributionType is only used on the frontend, derive it based on what percentage value is set on the rule
export const deriveRuleContributionType = (
    rule?: DeepPartial<MoneyMapRuleWithAccountDetails>,
    belongsToGroup?: boolean,
): MoneyMapRuleContributionType | undefined => {
    if (belongsToGroup) {
        // Default to percentage for rules belonging to groups
        return MoneyMapRuleContributionType.PERCENTAGE_OF_REMAINING_FUNDS;
    }

    const percentage = rule?.percentage;

    if (percentage == null) {
        return undefined;
    }

    // Percentages are stored as decimal values on the rule (and for some reason "percentage" comes back as a string from the backend, even though it is typed as a number)
    if (castToDecimal(percentage) === 1) {
        return MoneyMapRuleContributionType.ALL_REMAINING_FUNDS;
    }

    return MoneyMapRuleContributionType.PERCENTAGE_OF_REMAINING_FUNDS;
};

// Bills, spend, and goal rules are all priority ordered -- however UI treats goals as being 1-indexed
// Function to convert from actual priority to synthetic priority to display goal
// E.g. if rules = [1 bill rule, 1 spend rule, 1 goal rule], convert goal of priority 3 to show synthetic priority 1
export const getSyntheticGoalPriorityFromActual = (
    priority: number,
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
): number => {
    const numNonGoalRules = (moneyMap?.moneyMapRules ?? []).filter(
        (rule) =>
            rule.type === MoneyMapRuleType.BILLS ||
            rule.type === MoneyMapRuleType.SPEND,
    ).length;
    return priority - numNonGoalRules;
};

// The inverse of the function above.
// E.g. if rules = [1 bill rule, 1 spend rule, 1 goal rule], convert goal of synthetic priority 1 to be actual priority 3
export const getActualGoalPriorityFromSynthetic = (
    priority: number,
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>,
) => {
    const numNonGoalRules = (moneyMap?.moneyMapRules ?? []).filter(
        (rule) =>
            rule.type === MoneyMapRuleType.BILLS ||
            rule.type === MoneyMapRuleType.SPEND,
    ).length;
    return priority + numNonGoalRules;
};

const normalizeInsertionPriority = (
    priority: number,
    maxPriority: number,
): number => {
    if (!Number.isFinite(priority)) {
        return maxPriority;
    }

    return Math.min(Math.max(Math.trunc(priority), 1), maxPriority);
};

// When adding a new rule/updating an existing rule/deleting a rule, cascade priority increments/decrements to the rest of the rules and groups
// Returns the complete set of rules and groups with updated priorities.
export const updateRuleAndGroupPriorities = <
    T extends
        | MoneyMapRuleUpsertDto
        | DeepPartial<MoneyMapRuleWithAccountDetails>,
>(
    updatedRule: T,
    allRules: Array<DeepPartial<MoneyMapRule>>, // This should contain the version of the previous rule (if it exists)
    allGroups: Array<DeepPartial<MoneyMapRuleGroup>>,
    operation: 'upsert' | 'delete',
): {
    rules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    groups: Array<DeepPartial<MoneyMapRuleGroup>>;
} => {
    const updatedRulePriority = updatedRule.priority;
    const updatedRuleBelongsToGroup = updatedRule.moneyMapRuleGroupId != null;

    const allRulesWithUpdatedRule = uniqBy([updatedRule, ...allRules], 'id');
    const allRulesWithoutUpdatedRule = allRules.filter(
        (rule) => rule.id !== updatedRule.id,
    );

    // Short circuit if new rule priority doesn't exist (shouldn't happen)
    if (updatedRulePriority == null) {
        return {
            rules: allRules,
            groups: allGroups,
        };
    }

    // Includes rules not in groups, and groups
    const orderedRulesAndGroups = prioritySortRulesAndGroups(
        allRules,
        allGroups,
    );

    const normalizedRulePriority = normalizeInsertionPriority(
        updatedRulePriority,
        orderedRulesAndGroups.length + 1,
    );

    const previousRuleIndex = orderedRulesAndGroups.findIndex(
        (rule) => rule.id === updatedRule.id,
    );

    const updatedRulesAndGroups = [...orderedRulesAndGroups];

    if (previousRuleIndex !== -1) {
        // If previous rule exists in array, remove it
        updatedRulesAndGroups.splice(previousRuleIndex, 1);
    }

    if (operation === 'upsert' && !updatedRuleBelongsToGroup) {
        // If upserting rule (that is not in a group), add it in at the next placement
        updatedRulesAndGroups.splice(normalizedRulePriority - 1, 0, {
            ...updatedRule,
            entityType: 'rule',
        });
    }

    // Also update group priorities, and corresponding rules to match those group priorities
    const updatedRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>> = [];
    const updatedGroups: Array<DeepPartial<MoneyMapRuleGroup>> = [];

    updatedRulesAndGroups.forEach(({ entityType, ...item }, index) => {
        const priority = index + 1;
        if (entityType === 'rule') {
            updatedRules.push({
                ...item,
                priority,
            });
        } else {
            const updatedRuleBelongsToThisGroup =
                updatedRule.moneyMapRuleGroupId === item.id;

            updatedGroups.push({
                ...(item as DeepPartial<MoneyMapRuleGroup>),
                // If updated rule belongs to this group, do not change the group priority
                priority: updatedRuleBelongsToThisGroup
                    ? item.priority
                    : priority,
            });

            // Update rules in group, exclude updated rule if deleting it
            const rulesInGroup = (
                operation === 'delete'
                    ? allRulesWithoutUpdatedRule
                    : allRulesWithUpdatedRule
            ).filter((rule) => rule.moneyMapRuleGroupId === item.id);
            rulesInGroup.forEach((rule) => {
                updatedRules.push({
                    ...rule,
                    // If updated rule belongs to this group, do not change the rules in group's priorities
                    priority: updatedRuleBelongsToThisGroup
                        ? item.priority
                        : priority,
                });
            });
        }
    });

    return {
        rules: updatedRules,
        groups: updatedGroups,
    };
};

// When adding a new group/updating an existing group/deleting a group, cascade priority increments/decrements to the rest of the rules and groups
// Returns the complete set of rules and groups with updated priorities.
export const updateRuleAndGroupPrioritiesForGroupUpdate = (
    updatedGroup: DeepPartial<MoneyMapRule>,
    allRules: Array<DeepPartial<MoneyMapRule>>,
    allGroups: Array<DeepPartial<MoneyMapRuleGroup>>, // This should contain the version of the previous group (if it exists)
    operation: 'upsert' | 'delete',
): {
    rules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    groups: Array<DeepPartial<MoneyMapRuleGroup>>;
} => {
    const updatedGroupPriority = updatedGroup.priority;

    // Short circuit if new group priority doesn't exist (shouldn't happen)
    if (updatedGroupPriority == null) {
        return {
            rules: allRules,
            groups: allGroups,
        };
    }

    // Includes rules not in groups, and groups
    const orderedRulesAndGroups = prioritySortRulesAndGroups(
        allRules,
        allGroups,
    );

    const normalizedGroupPriority = normalizeInsertionPriority(
        updatedGroupPriority,
        orderedRulesAndGroups.length + 1,
    );

    const previousGroupIndex = orderedRulesAndGroups.findIndex(
        (group) => group.id === updatedGroup.id,
    );

    const updatedRulesAndGroups = [...orderedRulesAndGroups];

    if (previousGroupIndex !== -1) {
        // If previous group exists in array, remove it
        updatedRulesAndGroups.splice(previousGroupIndex, 1);
    }

    if (operation === 'upsert') {
        // If upserting rule (that is not in a group), add it in at the next placement
        updatedRulesAndGroups.splice(normalizedGroupPriority - 1, 0, {
            ...updatedGroup,
            percentage: updatedGroup.percentage ?? undefined, // addresses type mismatch
            entityType: 'group',
        });
    }

    // Also update group priorities, and corresponding rules to match those group priorities
    const updatedRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>> = [];
    const updatedGroups: Array<DeepPartial<MoneyMapRuleGroup>> = [];

    updatedRulesAndGroups.forEach(({ entityType, ...item }, index) => {
        const priority = index + 1;
        if (entityType === 'rule') {
            updatedRules.push({
                ...item,
                priority,
            });
        } else {
            updatedGroups.push({
                ...(item as DeepPartial<MoneyMapRuleGroup>),
                priority,
            });

            // Update rules in group, exclude updated rule if deleting it
            const rulesInGroup = allRules.filter(
                (rule) => rule.moneyMapRuleGroupId === item.id,
            );
            rulesInGroup.forEach((rule) => {
                updatedRules.push({
                    ...rule,
                    priority,
                });
            });
        }
    });

    return {
        rules: updatedRules,
        groups: updatedGroups,
    };
};

export const stripMoneyMapAccountFromRules = (
    allRules: Array<DeepPartial<MoneyMapRuleWithAccount>>,
): Array<DeepPartial<MoneyMapRule>> => {
    return allRules.map((rule) => {
        return omit(rule, 'moneyMapAccount');
    });
};

export const removeTransferAccountsFromRulesAccounts = (
    rules: Array<DeepPartial<MoneyMapRuleWithAccount>>,
) => {
    return rules.map((rule) => {
        if (rule.moneyMapAccount?.moneyMapAccountIdForTransfer != null) {
            return {
                ...rule,
                moneyMapAccount: {
                    ...rule.moneyMapAccount,
                    moneyMapAccountForTransfer: undefined,
                    moneyMapAccountIdForTransfer: null,
                },
            };
        }

        return rule;
    });
};

export const getCategorizedRulesAndTotals = (
    moneyMap?: DeepPartial<MoneyMapWithRulesAndAccounts>,
    accounts?: MoneyMapAccountWithAccountDetails[],
): {
    billRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    spendRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    goalRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    otherAccountRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    billsTotalCents: number | null;
    spendTotalCents: number | null;
    goalsTotalCents: number | null;
} => {
    if (moneyMap == null || accounts == null) {
        return {
            billRules: [],
            spendRules: [],
            goalRules: [],
            otherAccountRules: [],
            billsTotalCents: null,
            spendTotalCents: null,
            goalsTotalCents: null,
        };
    }

    const accountsKeyedById = keyBy(accounts, 'id');

    const billRules = getRulesWithAccountsForCategory(
        moneyMap,
        accountsKeyedById,
        MoneyMapCategory.BILLS,
    );
    const spendRules = getRulesWithAccountsForCategory(
        moneyMap,
        accountsKeyedById,
        MoneyMapCategory.SPEND,
    );
    const goalRules = getRulesWithAccountsForCategory(
        moneyMap,
        accountsKeyedById,
        MoneyMapCategory.GOALS,
    );
    const otherAccountRules = getRulesWithAccountsForCategory(
        moneyMap,
        accountsKeyedById,
        MoneyMapCategory.OTHER_ACCOUNTS,
    );

    const billsTotalCents =
        billRules.length > 0 ? getTotalAmountCentsForRules(billRules) : null;
    const spendTotalCents =
        billRules.length > 0 ? getTotalAmountCentsForRules(spendRules) : null;
    const goalsTotalCents =
        moneyMap.monthlyIncomeCents != null &&
        billsTotalCents != null &&
        spendTotalCents != null
            ? moneyMap.monthlyIncomeCents - billsTotalCents - spendTotalCents
            : null;

    return {
        billRules,
        spendRules,
        goalRules,
        otherAccountRules,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    };
};

// Order ungrouped rules and groups by priority
export const prioritySortRulesAndGroups = (
    rules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>,
    groups: Array<DeepPartial<MoneyMapRuleGroup>>,
): RuleOrGroup[] => {
    const ungroupedTypeRules: RuleWithEntityType[] = rules
        .filter((rule) => rule.moneyMapRuleGroupId == null)
        .map((rule) => ({ ...rule, entityType: 'rule' }));

    const typedGroups: GroupWithEntityType[] = groups.map((group) => ({
        ...group,
        entityType: 'group',
    }));

    return orderBy([...ungroupedTypeRules, ...typedGroups], 'priority');
};
